import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import BlockNumberModel from './BlockNumberModel';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface BlockNumbers {
  block_id: number;
  block_number: string;
  block_status: number;
}

const BlockNumbers: React.FC = () => {
  const [blocknumbers, setBlockNumbers] = useState<BlockNumbers[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<BlockNumbers | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchBlockNumbers = async () => {
    try {
      const data = await apiRequest<BlockNumbers[]>({
        method: 'get',
        url: 'block',
      });
      setBlockNumbers(data);
    } catch (error) {
      showError('Failed to fetch block number');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchBlockNumbers();
  }, []);

  const handleEditClick = (record: BlockNumbers) => {
    setIsEditMode(true);
    setInitialValues({
      block_id: record.block_id,
      block_number: record.block_number,
      block_status: record.block_status,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchBlockNumbers();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (block_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-block/${block_id}`,
      });
      showSuccess('block number deleted');
      fetchBlockNumbers();
    } catch {
      showError('Failed to delete block number');
    }
  };

  const handleStatusToggle = async (BlockNumberId: number) => {
    setBlockNumbers((prev) =>
      prev.map((item) =>
        item.block_id === BlockNumberId
          ? { ...item, block_status: item.block_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-block/${BlockNumberId}`,
      });
      showSuccess('block number status updated');
    } catch (error) {
      showError('Failed to update status');
      setBlockNumbers((prev) =>
        prev.map((item) =>
          item.block_id === BlockNumberId
            ? { ...item, block_status: item.block_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<BlockNumbers>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<BlockNumbers> = [
    {
      title: 'Block Number',
      dataIndex: 'block_number',
      key: 'block_number',
      filters: Array.from(new Set(blocknumbers.map((s) => s.block_number))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.block_number || null,
      onFilter: (value, record) => record.block_number.includes(value as string),
      sorter: (a, b) => a.block_number.localeCompare(b.block_number),
      sortOrder: sortedInfo.columnKey === 'block_number' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.block_number}</div>,
    },
  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: BlockNumbers) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.block_status}
                onChange={() => handleStatusToggle(record.block_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this block number?"
              onConfirm={() => handleDelete(record.block_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Block Numbers</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Block Number
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<BlockNumbers>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="block_id"
          dataSource={blocknumbers.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 11 }}
          locale={{ emptyText: 'No block number found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, blocknumbers.length)} of{' '}
                {blocknumbers.length}
              </span>
              <Pagination
                {...pagination}
                total={blocknumbers.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <BlockNumberModel
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default BlockNumbers;